import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Heading2, Heading3, Italic, Link2, List, ListOrdered, Quote, Redo2, Undo2 } from "lucide-react";

const Tool = ({ onClick, active, label, icon: Icon }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} aria-label={label} title={label} data-testid={`editor-tool-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`h-8 w-8 grid place-items-center transition-colors ${active ? "bg-foreground text-background" : "hover:bg-muted"}`}>
    <Icon className="h-4 w-4" />
  </button>
);

export const RichEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false } })],
    content: value || "",
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "article-body min-h-[420px] px-6 py-5", "data-testid": "editor-body" } },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const url = window.prompt("Pega el enlace", editor.getAttributes("link").href || "https://");
    if (url === null) return;
    url ? c().extendMarkRange("link").setLink({ href: url }).run() : c().unsetLink().run();
  };

  return (
    <div className="border border-border bg-card" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5 sticky top-14 lg:top-0 bg-card z-10">
        <Tool label="Negrita" icon={Bold} active={editor.isActive("bold")} onClick={() => c().toggleBold().run()} />
        <Tool label="Cursiva" icon={Italic} active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()} />
        <span className="w-px h-5 bg-border mx-1" />
        <Tool label="Subtitulo" icon={Heading2} active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} />
        <Tool label="Subtitulo menor" icon={Heading3} active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} />
        <Tool label="Lista" icon={List} active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} />
        <Tool label="Lista numerada" icon={ListOrdered} active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()} />
        <Tool label="Cita" icon={Quote} active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()} />
        <Tool label="Enlace" icon={Link2} active={editor.isActive("link")} onClick={setLink} />
        <span className="w-px h-5 bg-border mx-1" />
        <Tool label="Deshacer" icon={Undo2} onClick={() => c().undo().run()} />
        <Tool label="Rehacer" icon={Redo2} onClick={() => c().redo().run()} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};
